import { useEffect, useId, useRef } from 'react';
import { SvgIcon } from '../../components/icons';
import { useDsTheme } from '../ThemeProvider';
import { THEMES } from '../themes';
import { IconButton, OverlayLayer, SegmentedControl, Switch } from '../primitives';

type SettingsPanelProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Display settings.
 *
 * A slide-over rather than a modal: changing theme or density is something you
 * want to *watch* happen to the page behind it, so blocking the view with a
 * centred dialog would defeat the purpose. It is still a focus-trapped
 * `role="dialog"` — it is a modal interaction, just positioned at the edge.
 */
export const SettingsPanel = ({ open, onClose }: SettingsPanelProps) => {
  const {
    theme,
    setTheme,
    mode,
    setMode,
    density,
    setDensity,
    reduceMotion,
    setReduceMotion,
  } = useDsTheme();

  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;

    restoreRef.current = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const timer = window.setTimeout(() => {
      panelRef.current
        ?.querySelector<HTMLElement>('button, input, select')
        ?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
      restoreRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <OverlayLayer>
      <div
        className="ds-scrim"
        style={{ zIndex: 1340 }}
        onMouseDown={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className="ds-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="ds-panel-header">
          <h2 id={titleId} className="ds-section-title">
            Display settings
          </h2>
          <IconButton label="Close settings" size="sm" onClick={onClose}>
            <SvgIcon name="close" size={16} />
          </IconButton>
        </div>

        <div className="ds-panel-body ds-scroll">
          <div className="ds-panel-group">
            <div className="ds-eyebrow" style={{ marginBottom: 8 }}>
              Design direction
            </div>
            <div className="flex flex-col gap-2">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="ds-theme-option"
                  aria-pressed={t.id === theme}
                  onClick={() => setTheme(t.id)}
                >
                  <span className="ds-swatches" aria-hidden="true">
                    {t.swatches.map((c) => (
                      <span
                        key={c}
                        className="ds-swatch"
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className="block"
                      style={{ fontSize: 'var(--ds-fs-sm)', fontWeight: 600 }}
                    >
                      {t.name}
                    </span>
                    <span
                      className="block ds-muted"
                      style={{ fontSize: 'var(--ds-fs-xs)' }}
                    >
                      {t.tagline}
                    </span>
                  </span>
                  {t.id === theme && (
                    <SvgIcon name="check" size={15} aria-hidden="true" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="ds-panel-group">
            <div className="ds-eyebrow" style={{ marginBottom: 8 }}>
              Appearance
            </div>
            <SegmentedControl
              label="Colour mode"
              value={mode}
              onChange={setMode}
              options={[
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
              ]}
            />
          </div>

          <div className="ds-panel-group">
            <div className="ds-eyebrow" style={{ marginBottom: 8 }}>
              Density
            </div>
            <SegmentedControl
              label="Interface density"
              value={density}
              onChange={setDensity}
              options={[
                { value: 'compact', label: 'Compact' },
                { value: 'comfortable', label: 'Comfortable' },
              ]}
            />
            <p
              className="ds-muted"
              style={{ fontSize: 'var(--ds-fs-xs)', marginTop: 6 }}
            >
              Compact fits roughly 40% more rows on screen. Touch devices keep
              larger hit targets either way.
            </p>
          </div>

          <div className="ds-panel-group">
            <div className="ds-eyebrow" style={{ marginBottom: 8 }}>
              Motion
            </div>
            <Switch
              label="Reduce motion"
              checked={reduceMotion}
              onChange={(e) => setReduceMotion(e.target.checked)}
            />
            <p
              className="ds-muted"
              style={{ fontSize: 'var(--ds-fs-xs)', marginTop: 6 }}
            >
              Disables the Liquid Mercury sheen and the Tactile press spring.
              Your OS setting is always respected on top of this.
            </p>
          </div>
        </div>
      </div>
    </OverlayLayer>
  );
};
